import { useEffect, useState, useCallback, useMemo } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Edit, Truck, Package, TrendingUp, DollarSign } from "lucide-react";
import { logger } from "@/lib/logger";

const STAGES_LABEL = {
  artwork_upload: "Artwork Upload", artwork_review: "Artwork Review",
  proof_approval: "Proof Approval", production_queue: "Production Queue",
  printing: "Printing", quality_inspection: "Quality Check",
  packaging: "Packaging", shipping: "Shipping",
  delivered: "Delivered", completed: "Completed",
};
const STAGE_ORDER = Object.keys(STAGES_LABEL);
const NEXT = { artwork_upload: "artwork_review", artwork_review: "proof_approval", proof_approval: "production_queue", production_queue: "printing", printing: "quality_inspection", quality_inspection: "packaging", packaging: "shipping", shipping: "delivered", delivered: "completed" };

// ============= PRODUCTION BOARD =============
export function ProductionBoard() {
  const [board, setBoard] = useState({});
  const refresh = useCallback(() => api.get("/production/board").then(r => setBoard(r.data)).catch(err => logger.error("Board load failed:", err)), []);
  useEffect(() => { refresh(); }, [refresh]);
  const advance = async (o) => {
    const next = NEXT[o.workflow_stage];
    if (!next) return;
    await api.patch(`/orders/${o.id}/status`, { status: next });
    toast.success(`Moved to ${STAGES_LABEL[next]}`);
    refresh();
  };
  const assign = async (o) => {
    const email = prompt("Assign to (email)", o.assigned_to || "") || "";
    const priority = prompt("Priority (low | normal | high | rush)", o.priority || "normal") || "normal";
    const notes = prompt("Production notes (optional)", o.production_notes || "") || "";
    await api.patch(`/orders/${o.id}/assign`, { assigned_to: email, priority, production_notes: notes });
    toast.success("Assigned"); refresh();
  };
  return (
    <div>
      <p className="text-sm text-[#555] mb-6">Kanban view of every paid order in production. Click a card to advance it.</p>
      <div className="grid gap-4 overflow-x-auto" style={{ gridTemplateColumns: `repeat(${STAGE_ORDER.length}, minmax(200px, 1fr))` }}>
        {STAGE_ORDER.map(stage => (
          <div key={stage} className="bg-[#FAFAFA] border border-[#E5E5E5] p-3 min-h-[400px]" data-testid={`prod-col-${stage}`}>
            <p className="text-[10px] font-mono tracking-[0.25em] uppercase text-[#C5A059] mb-3">{STAGES_LABEL[stage]} <span className="text-[#888]">({(board[stage]||[]).length})</span></p>
            <div className="space-y-2">
              {(board[stage] || []).map(o => (
                <div key={o.id} className="bg-white border border-[#E5E5E5] p-3 text-xs" data-testid={`prod-card-${o.id}`}>
                  <div className="flex justify-between">
                    <p className="font-mono text-[#C5A059]">#{o.id.slice(0,8).toUpperCase()}</p>
                    {o.priority && o.priority !== "normal" && <span className={`text-[9px] px-1 py-0.5 font-mono uppercase ${o.priority === "rush" ? "bg-[#9A1F1F] text-white" : "bg-[#C5A059] text-[#0a0a0a]"}`}>{o.priority}</span>}
                  </div>
                  <p className="font-serif text-sm mt-1 truncate">{o.customer_email}</p>
                  <p className="font-mono text-[#888]">${o.amount.toFixed(2)}</p>
                  {o.assigned_to && <p className="text-[10px] text-[#555] mt-1">→ {o.assigned_to}</p>}
                  <div className="flex gap-1 mt-2 pt-2 border-t border-[#F5F5F5]">
                    <button onClick={() => assign(o)} data-testid={`prod-assign-${o.id}`} className="flex-1 text-[10px] font-mono tracking-widest uppercase border border-[#E5E5E5] py-1 hover:border-[#0a0a0a]">Assign</button>
                    {NEXT[o.workflow_stage] && <button onClick={() => advance(o)} data-testid={`prod-advance-${o.id}`} className="flex-1 text-[10px] font-mono tracking-widest uppercase bg-[#0a0a0a] text-white py-1">→ Next</button>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ============= REPORTS =============
export function Reports() {
  const [sales, setSales] = useState(null);
  const [top, setTop] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [prod, setProd] = useState({});
  const [days, setDays] = useState(30);
  useEffect(() => {
    let cancelled = false;
    Promise.all([
      api.get(`/reports/sales?days=${days}`),
      api.get("/reports/top-products"),
      api.get("/reports/top-customers"),
      api.get("/reports/production"),
    ]).then(([s, t, c, p]) => {
      if (cancelled) return;
      setSales(s.data); setTop(t.data); setCustomers(c.data); setProd(p.data);
    }).catch(err => logger.error("Reports load failed:", err));
    return () => { cancelled = true; };
  }, [days]);
  const max = sales && sales.series.length ? Math.max(...sales.series.map(s => s.revenue), 1) : 1;
  const exportCSV = (rows, cols, name) => {
    const csv = [cols.join(","), ...rows.map(r => cols.map(c => `"${String(r[c] ?? "").replace(/"/g,'""')}"`).join(","))].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = `${name}.csv`; a.click();
  };
  return (
    <div className="space-y-10">
      <div className="flex items-center gap-4">
        <label className="text-xs font-mono uppercase tracking-widest">Period</label>
        <select value={days} onChange={e => setDays(Number(e.target.value))} className="border border-[#E5E5E5] bg-white px-3 py-2 text-sm" data-testid="report-period">
          <option value={7}>Last 7 days</option><option value={30}>Last 30 days</option><option value={90}>Last 90 days</option><option value={365}>Last 365 days</option>
        </select>
      </div>
      {sales && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="border border-[#E5E5E5] p-6"><div className="flex items-center gap-2 text-[10px] font-mono tracking-[0.3em] uppercase text-[#C5A059]"><DollarSign size={12}/> Revenue</div><p className="font-serif text-4xl mt-3" data-testid="report-revenue">${sales.total_revenue.toFixed(2)}</p></div>
          <div className="border border-[#E5E5E5] p-6"><div className="flex items-center gap-2 text-[10px] font-mono tracking-[0.3em] uppercase text-[#C5A059]"><Package size={12}/> Orders</div><p className="font-serif text-4xl mt-3" data-testid="report-orders">{sales.order_count}</p></div>
          <div className="border border-[#E5E5E5] p-6"><div className="flex items-center gap-2 text-[10px] font-mono tracking-[0.3em] uppercase text-[#C5A059]"><TrendingUp size={12}/> Avg. Order</div><p className="font-serif text-4xl mt-3">${sales.order_count ? (sales.total_revenue / sales.order_count).toFixed(2) : "0.00"}</p></div>
        </div>
      )}
      {sales && sales.series.length > 0 && (
        <div className="border border-[#E5E5E5] p-6">
          <p className="text-xs font-mono uppercase tracking-widest text-[#C5A059] mb-4">Revenue over time</p>
          <div className="flex items-end gap-1 h-40">
            {sales.series.map(s => (
              <div key={s.date} className="flex-1 flex flex-col items-center gap-1 group">
                <div className="w-full bg-[#0a0a0a] hover:bg-[#C5A059] transition-colors" style={{ height: `${(s.revenue / max) * 100}%` }} title={`${s.date}: $${s.revenue}`} />
                <p className="text-[9px] font-mono text-[#888] opacity-0 group-hover:opacity-100">${s.revenue.toFixed(0)}</p>
              </div>
            ))}
          </div>
        </div>
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="border border-[#E5E5E5] p-6">
          <div className="flex justify-between items-center mb-4">
            <p className="text-xs font-mono uppercase tracking-widest text-[#C5A059]">Top Products</p>
            <button onClick={() => exportCSV(top, ["name","quantity","revenue"], "top-products")} data-testid="report-export-products" className="text-[10px] font-mono uppercase tracking-widest border border-[#E5E5E5] px-3 py-1 hover:border-[#0a0a0a]">Export CSV</button>
          </div>
          <div className="space-y-2 text-sm">{top.slice(0,10).map((p) => (
            <div key={p.name} className="flex justify-between border-b border-[#F5F5F5] py-2">
              <span>{p.name}</span><span className="font-mono text-[#C5A059]">${p.revenue.toFixed(2)}</span>
            </div>
          ))}{top.length === 0 && <p className="text-sm text-[#888]">No paid orders yet.</p>}</div>
        </div>
        <div className="border border-[#E5E5E5] p-6">
          <div className="flex justify-between items-center mb-4">
            <p className="text-xs font-mono uppercase tracking-widest text-[#C5A059]">Top Customers</p>
            <button onClick={() => exportCSV(customers, ["name","email","orders","revenue"], "top-customers")} data-testid="report-export-customers" className="text-[10px] font-mono uppercase tracking-widest border border-[#E5E5E5] px-3 py-1 hover:border-[#0a0a0a]">Export CSV</button>
          </div>
          <div className="space-y-2 text-sm">{customers.slice(0,10).map((c) => (
            <div key={c.email} className="flex justify-between border-b border-[#F5F5F5] py-2">
              <span className="truncate">{c.name || c.email}</span><span className="font-mono text-[#C5A059]">${c.revenue.toFixed(2)}</span>
            </div>
          ))}{customers.length === 0 && <p className="text-sm text-[#888]">No paid customers yet.</p>}</div>
        </div>
      </div>
      <div className="border border-[#E5E5E5] p-6">
        <p className="text-xs font-mono uppercase tracking-widest text-[#C5A059] mb-4">Production pipeline</p>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          {STAGE_ORDER.map(s => (
            <div key={s} className="border border-[#E5E5E5] p-3"><p className="text-[10px] font-mono uppercase text-[#888]">{STAGES_LABEL[s]}</p><p className="font-serif text-3xl mt-2">{prod[s] || 0}</p></div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ============= INVENTORY =============
export function InventoryTab() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ name: "", sku: "", stock: 0, low_stock_threshold: 10, unit: "each" });
  const [showForm, setShowForm] = useState(false);
  const refresh = useCallback(() => api.get("/inventory").then(r => setItems(r.data)).catch(err => logger.error("Inventory load failed:", err)), []);
  useEffect(() => { refresh(); }, [refresh]);
  const add = async (e) => {
    e.preventDefault();
    try { await api.post("/inventory", { ...form, stock: Number(form.stock), low_stock_threshold: Number(form.low_stock_threshold) }); toast.success("Added"); setShowForm(false); setForm({ name: "", sku: "", stock: 0, low_stock_threshold: 10, unit: "each" }); refresh(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const adjust = async (id) => {
    const delta = Number(prompt("Adjust stock by (e.g. +50 or -10)", "0") || 0);
    if (!delta) return;
    const reason = prompt("Reason (optional)") || "";
    try { await api.patch(`/inventory/${id}/adjust`, { delta, reason }); toast.success("Stock adjusted"); refresh(); }
    catch (e) { toast.error("Failed"); }
  };
  const del = async (id) => { if (!window.confirm("Delete?")) return; await api.delete(`/inventory/${id}`); refresh(); };
  return (
    <div>
      <button onClick={() => setShowForm(!showForm)} data-testid="inv-add-btn" className="mb-6 inline-flex items-center gap-2 bg-[#0a0a0a] text-white px-6 py-3 text-xs tracking-[0.25em] uppercase font-bold"><Plus size={14}/> {showForm ? "Cancel" : "Add Item"}</button>
      {showForm && (
        <form onSubmit={add} className="mb-6 border border-[#E5E5E5] p-6 grid grid-cols-2 md:grid-cols-5 gap-3">
          <input required placeholder="Name" value={form.name} onChange={e => setForm({...form, name: e.target.value})} data-testid="inv-name" className="border border-[#E5E5E5] px-3 py-2 text-sm md:col-span-2" />
          <input required placeholder="SKU" value={form.sku} onChange={e => setForm({...form, sku: e.target.value})} data-testid="inv-sku" className="border border-[#E5E5E5] px-3 py-2 text-sm" />
          <input required type="number" placeholder="Stock" value={form.stock} onChange={e => setForm({...form, stock: e.target.value})} data-testid="inv-stock" className="border border-[#E5E5E5] px-3 py-2 text-sm" />
          <input placeholder="Unit" value={form.unit} onChange={e => setForm({...form, unit: e.target.value})} className="border border-[#E5E5E5] px-3 py-2 text-sm" />
          <button data-testid="inv-save" className="md:col-span-5 bg-[#0a0a0a] text-white px-6 py-2 text-xs tracking-[0.25em] uppercase font-bold">Save</button>
        </form>
      )}
      <table className="w-full text-sm">
        <thead><tr className="border-b border-[#E5E5E5] text-left text-xs font-mono uppercase tracking-widest text-[#555]"><th className="py-3">Name</th><th>SKU</th><th>Stock</th><th>Threshold</th><th>Unit</th><th></th></tr></thead>
        <tbody>{items.map(i => {
          const low = i.stock <= i.low_stock_threshold;
          return (
            <tr key={i.id} className="border-b border-[#F5F5F5]" data-testid={`inv-row-${i.id}`}>
              <td className="py-3">{i.name}</td>
              <td className="font-mono text-xs">{i.sku}</td>
              <td className={low ? "text-[#9A1F1F] font-bold" : ""}>{i.stock} {low && "⚠"}</td>
              <td className="font-mono text-xs">{i.low_stock_threshold}</td>
              <td className="text-xs">{i.unit}</td>
              <td className="flex gap-2 py-2">
                <button onClick={() => adjust(i.id)} className="text-xs px-2 py-1 border border-[#E5E5E5]">Adjust</button>
                <button onClick={() => del(i.id)} className="text-xs px-2 py-1 border border-[#9A1F1F] text-[#9A1F1F]"><Trash2 size={12}/></button>
              </td>
            </tr>
          );
        })}</tbody>
      </table>
    </div>
  );
}

// ============= GENERIC CONFIG TAB =============
export function ConfigTab({ endpoint, label, fields, testid }) {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const empty = Object.fromEntries(fields.map(f => [f.k, f.default ?? ""]));
  const [form, setForm] = useState({ ...empty, active: true });
  const refresh = useCallback(() => api.get(`/${endpoint}`).then(r => setItems(r.data)).catch(err => logger.error(`${endpoint} load failed:`, err)), [endpoint]);
  useEffect(() => { refresh(); }, [refresh]);
  const save = async (e) => {
    e.preventDefault();
    const payload = { active: true, ...form };
    for (const f of fields) if (f.type === "number") payload[f.k] = Number(payload[f.k]) || 0;
    try {
      if (editing) await api.patch(`/${endpoint}/${editing.id}`, payload);
      else await api.post(`/${endpoint}`, payload);
      toast.success("Saved"); setShowForm(false); setEditing(null); setForm({ ...empty, active: true }); refresh();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const edit = (it) => { setEditing(it); setForm({ ...it }); setShowForm(true); };
  const del = async (id) => { if (!window.confirm("Delete?")) return; await api.delete(`/${endpoint}/${id}`); refresh(); };
  const summaries = useMemo(() => {
    const out = {};
    for (const it of items) {
      out[it.id] = fields
        .filter(f => f.k !== "name" && it[f.k] !== undefined && it[f.k] !== null && it[f.k] !== "")
        .map(f => `${f.label}: ${it[f.k]}`)
        .join(" · ");
    }
    return out;
  }, [items, fields]);
  return (
    <div>
      <button onClick={() => { setEditing(null); setForm({ ...empty, active: true }); setShowForm(!showForm); }} data-testid={`${testid}-add`} className="mb-6 inline-flex items-center gap-2 bg-[#0a0a0a] text-white px-6 py-3 text-xs tracking-[0.25em] uppercase font-bold"><Plus size={14}/> {showForm ? "Cancel" : `Add ${label}`}</button>
      {showForm && (
        <form onSubmit={save} className="mb-6 border border-[#E5E5E5] p-6 grid grid-cols-1 md:grid-cols-2 gap-3">
          {fields.map(f => (
            <div key={f.k}>
              <label className="text-[10px] font-mono tracking-[0.3em] uppercase">{f.label}</label>
              {f.type === "select" ? (
                <select value={form[f.k] || ""} onChange={e => setForm({...form, [f.k]: e.target.value})} className="mt-1 w-full border border-[#E5E5E5] bg-white px-3 py-2 text-sm">
                  <option value="">—</option>{f.options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              ) : (
                <input required={f.required} type={f.type || "text"} step={f.step || undefined} value={form[f.k] ?? ""} onChange={e => setForm({...form, [f.k]: e.target.value})} data-testid={`${testid}-${f.k}`} className="mt-1 w-full border border-[#E5E5E5] px-3 py-2 text-sm" />
              )}
            </div>
          ))}
          <label className="flex items-center gap-2 text-sm md:col-span-2"><input type="checkbox" checked={!!form.active} onChange={e => setForm({...form, active: e.target.checked})} /> Active</label>
          <button data-testid={`${testid}-save`} className="md:col-span-2 bg-[#0a0a0a] text-white px-6 py-2 text-xs tracking-[0.25em] uppercase font-bold">Save</button>
        </form>
      )}
      <div className="space-y-2">{items.map(it => (
        <div key={it.id} className="border border-[#E5E5E5] p-4 flex flex-wrap justify-between items-start gap-3" data-testid={`${testid}-row-${it.id}`}>
          <div className="flex-1">
            <p className="font-serif text-lg">{it.name}{!it.active && <span className="ml-2 text-[10px] font-mono uppercase text-[#888]">(inactive)</span>}</p>
            <p className="text-xs text-[#555] font-mono">{summaries[it.id] || ""}</p>
          </div>
          <div className="flex gap-2">
            <button onClick={() => edit(it)} className="text-xs px-3 py-2 border border-[#E5E5E5]"><Edit size={12}/></button>
            <button onClick={() => del(it.id)} className="text-xs px-3 py-2 border border-[#9A1F1F] text-[#9A1F1F]"><Trash2 size={12}/></button>
          </div>
        </div>
      ))}</div>
    </div>
  );
}

// ============= SHIPMENTS =============
export function ShipmentsTab() {
  const [shipments, setShipments] = useState([]);
  const [orders, setOrders] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ order_id: "", carrier: "usps", service: "", tracking_number: "", label_url: "", notes: "" });
  const refresh = useCallback(() => {
    api.get("/shipments").then(r => setShipments(r.data)).catch(err => logger.error("Shipments load failed:", err));
    api.get("/orders").then(r => setOrders(r.data.filter(o => o.payment_status === "paid"))).catch(err => logger.error("Orders load failed:", err));
  }, []);
  useEffect(() => { refresh(); }, [refresh]);
  const save = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/orders/${form.order_id}/shipments`, { order_id: form.order_id, carrier: form.carrier, service: form.service, tracking_number: form.tracking_number, label_url: form.label_url, notes: form.notes });
      toast.success("Shipment created — customer notified"); setShowForm(false);
      setForm({ order_id: "", carrier: "usps", service: "", tracking_number: "", label_url: "", notes: "" }); refresh();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const updateStatus = async (id) => {
    const status = prompt("Status: label_created | picked_up | in_transit | out_for_delivery | delivered | exception");
    if (!status) return;
    await api.patch(`/shipments/${id}/status`, { status }); toast.success("Updated"); refresh();
  };
  return (
    <div>
      <button onClick={() => setShowForm(!showForm)} data-testid="ship-add-btn" className="mb-6 inline-flex items-center gap-2 bg-[#0a0a0a] text-white px-6 py-3 text-xs tracking-[0.25em] uppercase font-bold"><Truck size={14}/> {showForm ? "Cancel" : "Create Shipment"}</button>
      {showForm && (
        <form onSubmit={save} className="mb-6 border border-[#E5E5E5] p-6 grid grid-cols-1 md:grid-cols-2 gap-3">
          <select required value={form.order_id} onChange={e => setForm({...form, order_id: e.target.value})} data-testid="ship-order" className="border border-[#E5E5E5] bg-white px-3 py-2 text-sm md:col-span-2">
            <option value="">Select paid order…</option>
            {orders.map(o => <option key={o.id} value={o.id}>#{o.id.slice(0,8)} — {o.customer_email} — ${o.amount.toFixed(2)}</option>)}
          </select>
          <select value={form.carrier} onChange={e => setForm({...form, carrier: e.target.value})} data-testid="ship-carrier" className="border border-[#E5E5E5] bg-white px-3 py-2 text-sm"><option value="usps">USPS</option><option value="ups">UPS</option><option value="fedex">FedEx</option><option value="pickup">Studio Pickup</option><option value="local">Local Delivery</option></select>
          <input placeholder="Service (e.g. Ground)" value={form.service} onChange={e => setForm({...form, service: e.target.value})} className="border border-[#E5E5E5] px-3 py-2 text-sm" />
          <input required placeholder="Tracking number" value={form.tracking_number} onChange={e => setForm({...form, tracking_number: e.target.value})} data-testid="ship-tracking" className="border border-[#E5E5E5] px-3 py-2 text-sm md:col-span-2" />
          <input placeholder="Label URL (optional)" value={form.label_url} onChange={e => setForm({...form, label_url: e.target.value})} className="border border-[#E5E5E5] px-3 py-2 text-sm md:col-span-2" />
          <button data-testid="ship-save" className="md:col-span-2 bg-[#0a0a0a] text-white px-6 py-2 text-xs tracking-[0.25em] uppercase font-bold">Create & Notify Customer</button>
        </form>
      )}
      <div className="space-y-3">{shipments.map(s => (
        <div key={s.id} className="border border-[#E5E5E5] p-4 flex flex-wrap justify-between items-start gap-3" data-testid={`ship-row-${s.id}`}>
          <div>
            <p className="font-mono text-xs uppercase text-[#C5A059]">{s.status.replace(/_/g," ")} · {s.carrier.toUpperCase()}</p>
            <p className="font-serif text-lg mt-1">{s.tracking_number}</p>
            <p className="text-xs text-[#888]">Order #{s.order_id.slice(0,8).toUpperCase()} · {new Date(s.created_at).toLocaleDateString()}</p>
          </div>
          <button onClick={() => updateStatus(s.id)} className="text-xs font-mono uppercase tracking-widest border border-[#E5E5E5] px-3 py-2">Update Status</button>
        </div>
      ))}{shipments.length === 0 && <p className="text-sm text-[#888]">No shipments yet.</p>}</div>
    </div>
  );
}
